import { FC } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ArrowBack, Delete, Refresh } from '@mui/icons-material';
import { useAdminJobsController } from './AdminJobs.hook';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { DelayedJob, JobScheduler } from '@stores/JobStore';
import cronstrue from 'cronstrue';

const formatTimestamp = (millis: number | null | undefined): string => {
  if (!millis) return '—';
  return DateTime.fromMillis(millis).toFormat('MM/dd/yyyy hh:mm a');
};

const formatCron = (pattern: string, tz: string | null): string => {
  try {
    const description = cronstrue.toString(pattern, { use24HourTimeFormat: false });
    const tzLabel = tz || 'UTC';
    return `${description} (${tzLabel})`;
  } catch {
    return pattern;
  }
};

const delayedJobColumns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
  {
    field: 'timestamp',
    headerName: 'Created',
    flex: 1,
    minWidth: 180,
    valueFormatter: (value: number) => formatTimestamp(value),
  },
  {
    field: 'scheduledTime',
    headerName: 'Scheduled Time',
    flex: 1,
    minWidth: 180,
    valueGetter: (_value: unknown, row: DelayedJob) => {
      if (row.timestamp && row.delay) return row.timestamp + row.delay;
      return null;
    },
    valueFormatter: (value: number | null) => formatTimestamp(value),
  },
];

const schedulerColumns = (onDelete: (scheduler: JobScheduler) => void): GridColDef[] => [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
  {
    field: 'schedule',
    headerName: 'Schedule',
    flex: 1.2,
    minWidth: 250,
    valueGetter: (_value: unknown, row: JobScheduler) => formatCron(row.pattern, row.tz),
  },
  {
    field: 'next',
    headerName: 'Next Run',
    flex: 1,
    minWidth: 180,
    valueFormatter: (value: number) => formatTimestamp(value),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    disableColumnMenu: true,
    width: 80,
    renderCell: (params) => (
      <IconButton
        color="error"
        onClick={() => onDelete(params.row as JobScheduler)}
        aria-label={`Remove scheduler ${params.row.name}`}
        size="small"
      >
        <Delete />
      </IconButton>
    ),
  },
];

export const AdminJobs: FC = () => {
  const navigate = useNavigate();
  const { schedulers, delayedJobs, loading, confirmDialog, handleRemoveScheduler, handleCloseDialog, refresh } =
    useAdminJobsController();

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="section" sx={{ py: 2 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin')} sx={{ mb: 1 }}>
        Back to Admin
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Job Queue Manager</Typography>
        <Button startIcon={<Refresh />} variant="outlined" onClick={refresh}>
          Refresh
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Delayed Jobs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Jobs that are scheduled to run at a specific time in the future. To remove a delayed job, delete its scheduler
          below.
        </Typography>
        <DataGrid
          rows={delayedJobs}
          columns={delayedJobColumns}
          getRowId={(row) => row.id}
          density="compact"
          initialState={{
            sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Box>

      <Box sx={{ border: '2px solid', borderColor: 'error.main', borderRadius: 2, p: 3 }}>
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          Danger Zone — Removing a scheduler is irreversible and will stop notifications from being sent to
          participants.
        </Alert>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Job Schedulers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Repeating job definitions that run on a schedule. Removing a scheduler stops all future runs and removes its
          next queued instance above.
        </Typography>
        <DataGrid
          rows={schedulers}
          columns={schedulerColumns(handleRemoveScheduler)}
          getRowId={(row) => row.key}
          density="compact"
          initialState={{
            sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Box>

      <Dialog open={confirmDialog.open} onClose={handleCloseDialog}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={confirmDialog.onConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
